import debounce from 'debounce-promise'
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import ExploreReview from './ExploreReview'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import IconButton from 'LuxKit/Button/IconButton'
import Carousel from 'LuxKit/Carousel/Carousel'
import type { PhysicalHorizontalDirection, ScrollPosition } from 'LuxKit/Carousel/utils'
import {
  getElementContentWidth,
  getHorizontalScrollPosition,
  getLogicalScrollDeltaForPhysicalHorizontalDirection,
  getLogicalScrollLeft,
  isPhysicalHorizontalArrowHidden,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import useTextDirection from 'hooks/useTextDirection'
import LineAngleLeftBIcon from 'LuxKit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import GooglePlaceRatingDisplay from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRatingDisplay'
import type { ExploreItemReview } from 'tripPlanner/types/common'
import useResizeObserver from 'hooks/useResizeObserver'

interface Props {
  rating: number | undefined
  ratingsCount: number | undefined
  reviews: Array<ExploreItemReview> | undefined
}

const ReviewCarousel = styled(Carousel)<{ $height?: number }>`
  > div {
    box-sizing: content-box;
    height: ${props => props.$height === undefined ? 'auto' : `${props.$height}px`};
    grid-template-rows: minmax(0, 1fr);
    overflow-y: hidden;
    transition: height 240ms ease-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    [data-review-content] {
      align-self: start;
    }
  }
`

function ExploreReviews({ rating, ratingsCount, reviews }: Props) {
  const intl = useIntl()
  const [position, setPosition] = useState<ScrollPosition>('start')
  const [carouselHeight, setCarouselHeight] = useState<number>()
  const [activeReview, setActiveReview] = useState<Element | null>(null)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const activeReviewRef = useRef<Element | null>(null)
  const textDirection = useTextDirection(carouselRef)

  const growToReview = useCallback((review: Element | undefined) => {
    if (!review) return
    const height = review.getBoundingClientRect().height
    setCarouselHeight(previousHeight => Math.max(previousHeight ?? 0, height))
  }, [])

  const handleReviewResize = useCallback((entry: ResizeObserverEntry) => {
    if (entry.target === activeReviewRef.current) {
      growToReview(entry.target)
    }
  }, [growToReview])

  useResizeObserver(activeReview, handleReviewResize)

  useLayoutEffect(() => {
    const carousel = carouselRef.current
    const firstReview = carousel?.querySelector('[data-review-content]')
    activeReviewRef.current = firstReview ?? null
    setActiveReview(firstReview ?? null)
    setCarouselHeight(firstReview?.getBoundingClientRect().height)
  }, [reviews])

  /** Based on logic from CardCarousel */
  const onCarouselArrowClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!carouselRef.current) {
      return
    }
    const carousel = carouselRef.current
    const direction = e.currentTarget.dataset.direction as PhysicalHorizontalDirection
    const contentWidth = getElementContentWidth(carousel)

    const nextLeft = getLogicalScrollLeft(carousel) +
      getLogicalScrollDeltaForPhysicalHorizontalDirection(textDirection, direction, contentWidth)

    scrollToLogicalPosition(carousel, {
      left: nextLeft,
      behavior: 'smooth',
    })
  }

  const updatePosition = useMemo(() => {
    return debounce(() => {
      if (carouselRef.current) {
        setPosition(getHorizontalScrollPosition(carouselRef.current))
      }
    }, 32)
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const onScroll = useCallback(() => {
    const carousel = carouselRef.current
    if (!carousel || !reviews?.length) return

    const reviewIndex = Math.min(
      reviews.length - 1,
      Math.max(0, Math.round(getLogicalScrollLeft(carousel) / getElementContentWidth(carousel))),
    )
    const nextActiveReview = carousel.querySelectorAll('[data-review-content]')[reviewIndex] ?? null
    activeReviewRef.current = nextActiveReview
    setActiveReview(nextActiveReview)
    growToReview(nextActiveReview ?? undefined)
    updatePosition()
  }, [reviews, growToReview, updatePosition])

  const showReviews = reviews && reviews.length > 0
  const showCarousel = reviews && reviews.length > 1

  return (
    <VerticalSpacer gap={16}>
      <Group direction="horizontal" horizontalAlign="space-between">
        <VerticalSpacer gap={4}>
          {showReviews && (
            <BodyText variant="medium" weight="bold">
              {intl.formatMessage({
                id: 'Xgdc-EMbdeb1-AzG_YA2',
                defaultMessage: 'Reviews',
                description: 'Heading for the reviews section of an explore item',
              })}
            </BodyText>
          )}
          {!!rating && (
            <GooglePlaceRatingDisplay
              rating={rating}
              ratingsCount={ratingsCount}
              size="S"
            />
          )}
        </VerticalSpacer>
        {showCarousel && (
          <div>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={onCarouselArrowClick}
              data-direction="left"
              disabled={isPhysicalHorizontalArrowHidden(position, textDirection, 'left')}
            >
              <LineAngleLeftBIcon rtlFlip={false} />
            </IconButton>
            <IconButton
              kind="tertiary"
              size="medium"
              shape="square"
              onClick={onCarouselArrowClick}
              data-direction="right"
              disabled={isPhysicalHorizontalArrowHidden(position, textDirection, 'right')}
            >
              <LineAngleRightBIcon rtlFlip={false} />
            </IconButton>
          </div>
        )}
      </Group>

      {showReviews && reviews.length === 1 && <ExploreReview review={reviews[0]} />}

      {showCarousel && (
        <div>
          <ReviewCarousel
            $height={carouselHeight}
            data-testid="explore-review-carousel"
            snap="start"
            gutterSize={0}
            width="100%"
            ref={carouselRef}
            onScroll={onScroll}
          >
            {reviews.map((review) => (
              <div key={`${review.authorUrl}-${review.publishTime}`} data-review-content>
                <ExploreReview review={review} />
              </div>
            ))}
          </ReviewCarousel>
        </div>
      )}
    </VerticalSpacer>
  )
}

export default ExploreReviews
